import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { resolveQuestionHtmlAssetUrl } from "../../../api";
import { MathInputTools } from "./TypeDrawQuestionInput";

/**
 * Renders a question image with answer boxes overlaid at stored fractional
 * positions. `values` is an index-aligned array of strings (one per box);
 * `onChange(nextValues)` is fired on every edit. When `readOnly` is set the
 * boxes show the submitted value (or `revealValues[i]` if provided) without
 * being editable.
 *
 * The answer font scales to each box's rendered height so the typed digit
 * fills the box (rather than a tiny fixed size).
 */
const ImageOverlayFillInput = ({
  overlayLayout,
  values = [],
  onChange,
  prefilled = [],
  readOnly = false,
  revealValues = null,
  showMathTools = false,
}) => {
  const imgRef = useRef(null);
  const inputRefs = useRef([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [imgHeight, setImgHeight] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [usedRawFallback, setUsedRawFallback] = useState(false);

  const imageUrl = overlayLayout?.image_url || "";
  const boxes = Array.isArray(overlayLayout?.blanks) ? overlayLayout.blanks : [];

  // resolveQuestionHtmlAssetUrl rewrites proxy/R2 URLs to the current env's asset base, which can
  // 404 even when the original URL is reachable. Render the rewritten URL first, then fall back to
  // the raw imageUrl on error before giving up — otherwise the image (the question's only content) is lost.
  const resolvedSrc = resolveQuestionHtmlAssetUrl(imageUrl) || imageUrl;
  const activeSrc = usedRawFallback ? imageUrl : resolvedSrc;

  useEffect(() => {
    setImgFailed(false);
    setUsedRawFallback(false);
  }, [imageUrl]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return undefined;
    const measure = () => setImgHeight(el.clientHeight || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [imageUrl]);

  const fallbackCount = boxes.length || (Array.isArray(values) ? values.length : 0);

  const setValue = (idx, val) => {
    if (readOnly || !onChange) return;
    const len = boxes.length || fallbackCount;
    const next = Array.from({ length: len }, (_, i) => (i === idx ? val : (values[i] ?? "")));
    onChange(next);
  };

  const mathTools = showMathTools && !readOnly && (boxes.length > 0 || fallbackCount > 0) ? (
    <MathInputTools
      getInput={() => inputRefs.current[activeIdx] ?? null}
      getValue={() => values[activeIdx] ?? ""}
      onChange={(newVal) => setValue(activeIdx, newVal)}
    />
  ) : null;

  // No usable image: render plain blank inputs so the question stays answerable, and render
  // nothing (rather than an empty div) when there are no blanks either.
  if (!imageUrl || imgFailed) {
    if (fallbackCount === 0) return null;
    return (
      <Box>
        {mathTools}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>
        {Array.from({ length: fallbackCount }, (_, idx) => {
          const shown = revealValues ? (revealValues[idx] ?? "") : (values[idx] ?? "");
          return (
            <Box key={idx} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {prefilled?.[idx] ? (
                <Box sx={{ fontSize: "0.7rem", color: "text.secondary", whiteSpace: "nowrap", mb: 0.25 }}>
                  {prefilled[idx]}
                </Box>
              ) : null}
              <Box
                component="input"
                type="text"
                inputMode={showMathTools ? undefined : "numeric"}
                value={shown}
                readOnly={readOnly}
                ref={(el) => { inputRefs.current[idx] = el; }}
                onFocus={() => setActiveIdx(idx)}
                onChange={(e) => setValue(idx, e.target.value)}
                sx={{
                  width: 64,
                  height: 48,
                  textAlign: "center",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  border: "2px solid",
                  borderColor: readOnly ? "success.main" : "primary.main",
                  borderRadius: 1,
                  bgcolor: readOnly ? "rgba(76,175,80,0.10)" : "rgba(255,255,255,0.92)",
                  color: readOnly ? "success.dark" : "text.primary",
                  outline: "none",
                  p: 0,
                }}
              />
            </Box>
          );
        })}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {mathTools}
      <Box sx={{ position: "relative", display: "inline-block", maxWidth: "100%", lineHeight: 0 }}>
      <img
        ref={imgRef}
        src={activeSrc}
        alt="Question"
        style={{ maxWidth: "100%", display: "block" }}
        onLoad={(e) => setImgHeight(e.currentTarget.clientHeight || 0)}
        onError={() => {
          if (!usedRawFallback && resolvedSrc !== imageUrl && imageUrl) {
            setUsedRawFallback(true);
          } else {
            setImgFailed(true);
          }
        }}
      />
      {boxes.map((b, idx) => {
        const shown = revealValues ? (revealValues[idx] ?? "") : (values[idx] ?? "");
        const isLine = b.style === "line";
        const lineRows = Math.max(1, parseInt(b.lines, 10) || 1);
        const isMultiLine = lineRows > 1;
        const boxPx = imgHeight > 0 ? imgHeight * (b.h ?? 0.08) : 0;
        // Each writing row gets at least row_gap px of height (set per box in the editor) so the
        // underlines have a comfortable gap; a multi-line box grows downward past its drawn height.
        const minRowPx = Math.max(16, parseInt(b.row_gap, 10) || 40);
        const rowPx = isMultiLine
          ? Math.max(boxPx > 0 ? boxPx / lineRows : 0, minRowPx)
          : boxPx;
        const multiLineHeightPx = rowPx * lineRows;
        // Fill ~58% of each writing row's height; fall back to a readable default before the image measures.
        const fontPx = rowPx > 0 ? Math.max(13, Math.round(rowPx * 0.5)) : (isMultiLine ? 16 : 18);
        const lineColor = readOnly ? "#2e7d32" : "#6366f1";
        return (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              left: `${(b.x ?? 0) * 100}%`,
              top: `${(b.y ?? 0) * 100}%`,
              width: `${(b.w ?? 0.1) * 100}%`,
              height: isMultiLine ? `${multiLineHeightPx}px` : `${(b.h ?? 0.08) * 100}%`,
            }}
          >
            {prefilled?.[idx] ? (
              <Box sx={{ position: "absolute", top: -16, left: 0, fontSize: "0.7rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                {prefilled[idx]}
              </Box>
            ) : null}
            {isMultiLine ? (
              <Box
                component="textarea"
                value={shown}
                readOnly={readOnly}
                rows={lineRows}
                ref={(el) => { inputRefs.current[idx] = el; }}
                onFocus={() => setActiveIdx(idx)}
                onChange={(e) => setValue(idx, e.target.value)}
                style={{
                  fontSize: `${fontPx}px`,
                  lineHeight: rowPx > 0 ? `${rowPx}px` : `${Math.round(fontPx * 1.7)}px`,
                  backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent calc(${100 / lineRows}% - 2px), ${lineColor} calc(${100 / lineRows}% - 2px), ${lineColor} ${100 / lineRows}%)`,
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  boxSizing: "border-box",
                  textAlign: "left",
                  color: readOnly ? "success.dark" : "text.primary",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  outline: "none",
                  resize: "none",
                  overflow: "hidden",
                  border: "none",
                  bgcolor: "transparent",
                  p: 0,
                }}
              />
            ) : (
              <Box
                component="input"
                type="text"
                inputMode={showMathTools ? undefined : "numeric"}
                value={shown}
                readOnly={readOnly}
                ref={(el) => { inputRefs.current[idx] = el; }}
                onFocus={() => setActiveIdx(idx)}
                onChange={(e) => setValue(idx, e.target.value)}
                style={{ fontSize: `${fontPx}px`, lineHeight: 1 }}
                sx={{
                  width: "100%",
                  height: "100%",
                  boxSizing: "border-box",
                  textAlign: "center",
                  color: readOnly ? "success.dark" : "text.primary",
                  fontWeight: 700,
                  outline: "none",
                  p: 0,
                  ...(isLine
                    ? {
                        border: "none",
                        borderBottom: readOnly ? "2px solid" : "2px dashed",
                        borderColor: readOnly ? "success.main" : "primary.main",
                        borderRadius: 0,
                        bgcolor: "transparent",
                      }
                    : {
                        border: "2px solid",
                        borderColor: readOnly ? "success.main" : "primary.main",
                        borderRadius: 1,
                        bgcolor: readOnly ? "rgba(76,175,80,0.10)" : "rgba(255,255,255,0.92)",
                      }),
                }}
              />
            )}
          </Box>
        );
      })}
      </Box>
    </Box>
  );
};

export default ImageOverlayFillInput;
